import { useState, useCallback, useEffect } from 'react';

/**
 * Hook for managing preview mode and preview data
 * @returns {Object} Preview state and handlers
 */
export function usePreviewData() {
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [patternExtractedData, setPatternExtractedData] = useState([]);
  const [metadataEditedData, setMetadataEditedData] = useState([]);
  const [findReplaceData, setFindReplaceData] = useState([]);
  const [currentPattern, setCurrentPattern] = useState('');
  
  // Handle preview data updates from different panels
  const handlePreviewDataUpdate = useCallback((enabled, extractedData, source) => {
    if (source === 'pattern') {
      // Data from Pattern Extraction panel
      // If explicitly disabled or empty data, clear pattern data
      if (!enabled || !extractedData || extractedData.length === 0) {
        setPatternExtractedData([]);
        setCurrentPattern('');
      } else {
        setPatternExtractedData(extractedData);
        setCurrentPattern(source);
      }
    } else if (source === 'metadata') {
      // Data from Metadata Editing panel
      setMetadataEditedData(extractedData || []);
    } else if (source === 'findreplace') {
      // Data from Find and Replace panel
      setFindReplaceData(extractedData || []);
    }
  }, []); // No dependencies to keep function stable
  
  // Effect to merge data whenever pattern, metadata, or find/replace data changes
  useEffect(() => {
    // If no data from any source, clear preview
    if (patternExtractedData.length === 0 && metadataEditedData.length === 0 && findReplaceData.length === 0) {
      setIsPreviewMode(false);
      setPreviewData([]);
      return;
    }

    // Merge data from all sources (later sources override earlier ones)
    const mergedData = [];
    const allIds = new Set([
      ...patternExtractedData.map(d => d.id),
      ...metadataEditedData.map(d => d.id),
      ...findReplaceData.map(d => d.id)
    ]);

    allIds.forEach(id => {
      const patternData = patternExtractedData.find(d => d.id === id) || {};
      const metadataData = metadataEditedData.find(d => d.id === id) || {};
      const findReplaceDataItem = findReplaceData.find(d => d.id === id) || {};

      // Merge data with precedence: find/replace > metadata > pattern
      mergedData.push({
        id,
        ...patternData,
        ...metadataData,
        ...findReplaceDataItem
      });
    });

    // Update preview state
    setIsPreviewMode(mergedData.length > 0);
    setPreviewData(mergedData);
  }, [patternExtractedData, metadataEditedData, findReplaceData]);
  
  // Cancel preview mode
  const cancelPreview = useCallback(() => {
    setIsPreviewMode(false);
    setPreviewData([]);
    setCurrentPattern('');
    setPatternExtractedData([]);
    setMetadataEditedData([]);
    setFindReplaceData([]);
  }, []);

  // Clear preview data
  const clearPreviewData = useCallback(() => {
    setPreviewData([]);
    setPatternExtractedData([]);
    setMetadataEditedData([]);
    setFindReplaceData([]);
    setCurrentPattern('');
  }, []);
  
  return {
    // States
    isPreviewMode,
    setIsPreviewMode,
    previewData,
    setPreviewData,
    patternExtractedData,
    setPatternExtractedData,
    metadataEditedData,
    setMetadataEditedData,
    findReplaceData,
    setFindReplaceData,
    currentPattern,
    setCurrentPattern,

    // Handlers
    handlePreviewDataUpdate,
    cancelPreview,
    clearPreviewData
  };
}